import { MouseEvent } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { CornerMarks } from "@/components/Backdrop";
import { contact } from "@/data/content";

export default function HeroEmblem() {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
    const shineX = useTransform(mx, [-0.5, 0.5], ["10%", "90%"]);
    const { scrollY } = useScroll();
    const parallaxY = useTransform(scrollY, [0, 600], [0, -70]);

    const onMove = (e: MouseEvent<HTMLDivElement>) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <motion.div
            style={{ y: parallaxY, perspective: 1400 }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
            data-testid="hero-emblem"
        >
            <motion.div
                onMouseMove={onMove}
                onMouseLeave={onLeave}
                style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                className="relative"
            >
                <div className="absolute inset-0 translate-x-6 translate-y-6 border border-[#5B93E8]/35" style={{ transform: "translateZ(-40px)" }} />
                <div className="absolute inset-0 translate-x-3 translate-y-3 bg-[#1D4F9C]/8" style={{ transform: "translateZ(-20px)" }} />
                <div className="certificate relative overflow-hidden p-10">
                    <span className="scanline" />
                    <motion.span
                        aria-hidden
                        style={{ left: shineX }}
                        className="pointer-events-none absolute top-0 h-full w-40 -translate-x-1/2 bg-gradient-to-r from-transparent via-white/70 to-transparent opacity-60 mix-blend-soft-light"
                    />
                    <CornerMarks color="#5B93E8" />
                    <div className="ruler-x mt-5 h-1.5 w-full opacity-40" />
                    <motion.div
                        className="mx-auto mt-4 w-full max-w-[320px] border border-white/10 bg-[#F2EEE4] p-6"
                        style={{ transform: "translateZ(40px)" }}
                    >
                        <motion.img
                            src="/logo.png"
                            alt="Infrasic Intelligence — shield, magnifying glass and scales of justice emblem"
                            className="animate-float-slow mx-auto w-full"
                            data-testid="hero-logo"
                        />
                    </motion.div>
                    <div className="mt-8 space-y-2.5 border-t border-[#243650] pt-6 font-tech text-[11px] tracking-wider text-[#8C97AC]">
                        <div className="flex items-center justify-between">
                            <span>STATUS</span>
                            <span className="flex items-center gap-2 font-semibold text-[#E8ECF4]">
                                <span className="relative flex h-2 w-2">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#1D4F9C] opacity-60" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#1D4F9C]" />
                                </span>
                                ACCEPTING MANDATES
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span>REGISTRY</span>
                            <span className="text-[#5B93E8]">DPIIT &middot; {contact.dpiit}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span>POSITIONING</span>
                            <span className="text-[#D9A84E]">INVESTIGATE. ANALYSE. TRACE.</span>
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
